/**
 * 整箱报价管理前端接口
 */

import { request } from '@/utils/http'
import { type BatchDeleteRequest, type PageResponse } from '@/api/types/common'
import {
  type InitiateConsultationRequest, type InqQuotPriceChargingItemVO,
  type QuotationBaseModel, type QuotationChargingItemVO,
  type QuotationPageRequest,
  type QuotationSaveRequest,
  type SupplierInquiryVO
} from './quotation-base'

/**
 * 整箱报价模型（继承基础报价模型）
 */
export interface QuotationFclModel extends QuotationBaseModel {
}

/**
 * 整箱报价分页查询请求参数
 */
export interface QuotationFclPageRequest extends QuotationPageRequest {
}

/**
 * 整箱报价保存请求参数
 */
export interface QuotationFclSaveRequest extends QuotationSaveRequest {
}

/**
 * 整箱报价修改请求参数
 */
export interface QuotationFclUpdateRequest extends QuotationSaveRequest {
  id: string
}

/**
 * 分页查询整箱报价列表
 */
export const pageQuotationFcl = (params: QuotationFclPageRequest) => {
  return request.post<PageResponse<QuotationFclModel>>('/price/quotation/fcl/v2/page', params)
}

/**
 * 根据ID获取整箱报价详情
 */
export const getQuotationFclById = (id: string) => {
  return request.post<QuotationFclModel>('/price/quotation/fcl/v2/get', { id })
}

/**
 * 根据询价编号获取整箱报价详情
 */
export const getQuotationFclByInquiryNo = (inquiryNo: string) => {
  return request.post<QuotationFclModel>('/price/quotation/fcl/v2/get', { inquiryNo })
}

/**
 * 根据报价编号获取整箱报价详情（通过分页查询实现）
 * 注意：后端暂未提供直接根据报价编号查询ID的接口，这里通过分页查询来实现
 */
export const getQuotationFclByQuoteNo = (quoteNo: string): Promise<QuotationFclModel | null> => {
  const searchRequest = {
    pageNum: 1,
    pageSize: 1,
    conditions: [{
      fieldName: 'quotationNo',
      operator: 'EQ',
      value: quoteNo
    }]
  }
  return request.post<PageResponse<QuotationFclModel>>('/price/quotation/fcl/v2/page', searchRequest)
    .then(response => {
      if (response.records && response.records.length > 0) {
        return response.records[0]
      }
      return null
    })
}

/**
 * 新增整箱报价
 */
export const saveQuotationFcl = (params: QuotationFclSaveRequest) => {
  return request.post<boolean>('/price/quotation/fcl/v2/save', params)
}

/**
 * 修改整箱报价
 */
export const updateQuotationFcl = (params: QuotationFclUpdateRequest) => {
  return request.post<boolean>('/price/quotation/fcl/v2/update', params)
}

/**
 * 批量删除整箱报价
 */
export const batchDeleteQuotationFcl = (params: BatchDeleteRequest) => {
  return request.post<boolean>('/price/quotation/fcl/v2/batch-delete', params)
}

/**
 * 发起新磋商
 * 针对指定的整箱报价单发起新的磋商记录,更新报价单磋商状态为"磋商中"
 */
export const initiateConsultationFcl = (params: InitiateConsultationRequest) => {
  return request.post<boolean>('/price/quotation/fcl/v2/initiateConsultation', params)
}

/**
 * 商户报价单加价保存
 */
export const businessQuotationMarkupFcl = (params: InqQuotPriceChargingItemVO) => {
  return request.post<InqQuotPriceChargingItemVO>('/price/quotation/fcl/v2/businessQuotationMarkup', params)
}

/**
 * 供应商报价单加价保存
 */
export const supplierQuotationMarkupFcl = (params: QuotationChargingItemVO) => {
  return request.post<QuotationChargingItemVO>('/price/quotation/fcl/v2/supplierQuotationMarkup', params)
}

/**
 * AI识别请求参数
 */
export interface AIParseTextRequest {
  text: string
  type?: string
}

/**
 * AI识别响应结果
 */
export interface AIParseSupplierQuotationResponse {
  /** ETD */
  etd?: string | null;
  /** ETA */
  eta?: string | null;
  /** 船名航次 */
  voyageFlight?: string | null;
  /** 运输天数 */
  transportationDays?: number | null;
  /** 供应商备注 */
  supplierRemarks?: string | null;
  /** 计费条目列表（仅供应商报价时返回） */
  chargingItemList?: QuotationChargingItemVO[];
}

/**
 * AI 地址 文本识别接口
 */
export const parseTextBySupplierQuotation = (
    params: AIParseTextRequest,
    config?: { timeout?: number }
) => {
  return request.post<AIParseSupplierQuotationResponse>('/vas/ai/supplierQuotation', params, config)
}

export interface ImportFromQuotationRequest {
  quotationId?: string
  quotationIds?: string[]
  surchargeSyncType?: number
  source?: number
}

export const importQuotationToRate = (params: ImportFromQuotationRequest) => {
  return request.post<any>('/price/fcl/import-from-quotation', params)
}

export interface ConfirmImportRequest {
  batchNo: number | string
  priceIds?: string[]
  source?: number
  quotationIds?: string[]
}

export const confirmImportFromQuotation = (params: ConfirmImportRequest) => {
  return request.post<any>('/price/fcl/confirm-import-from-quotation', params)
}

export const cancelImportFromQuotation = (params: ConfirmImportRequest) => {
  return request.post<any>('/price/fcl/cancel-import-from-quotation', params)
}

// ---- 匿名报价接口 ----

export interface LinkAccess {
  token: string
}

/** 附件信息 */
export interface BusinessFileVO {
  id?: string
  name?: string
  fileName?: string
  url?: string
  size?: number
  [key: string]: unknown
}

/** 箱型箱量（询价） */
export interface InquiryContainerVO {
  id?: string
  inquiryId?: string
  containerType?: string
  containerNum?: number
  temperatureStart?: number
  temperatureEnd?: number
  ventilationEnable?: boolean
  ventilationQuantity?: number
  humidity?: number
  needPrecooling?: boolean
  quantity?: number
  weight?: number
  weightUnit?: string
  length?: number
  width?: number
  height?: number
  dimensionsUnit?: string
  remarks?: string
  packageId?: number
  // RORO fields
  transportCategory?: number
  vehicleType?: string
  vehicleBrand?: string
  vehicleModel?: string
  equipmentName?: string
  isCrawler?: boolean
  isSelfPropelled?: boolean
  isOversized?: boolean
  canDeckLoad?: boolean
  specialFixingRequirement?: string
  // BULK fields
  cargoName?: string
  stowageFactor?: number
  liftingRequirement?: string
}

/** 询价基础信息（仅含匿名报价页需要的字段） */
export interface InquiryFclListVO {
  id?: string
  inquiryNo?: string
  status?: number
  carrierId?: string
  /** 船公司 ID 集合（FCL 多选，优先于单值 carrierId） */
  carrierIds?: Array<string | number> | null
  loadingCountryId?: string
  loadingCityId?: string
  loadingPortId?: string
  destinationCountryId?: string
  destinationCityId?: string
  destinationPortId?: string
  transferPortId1?: string
  transferPortId2?: string
  transferPortId3?: string
  directTransit?: number
  cargoReadyTimeType?: string
  cargoReadyTimeRange?: number
  cargoReadyTimeDate?: string
  cargoType?: number
  unNo?: number
  hazardClass?: number
  cargoDescription?: string
  hsCode?: string
  cargoValue?: number
  cargoValueCurrency?: string
  onePriceSupported?: boolean
  quotationDeadline?: string
  specialRequirements?: string
  serviceType?: string
  paymentTerm?: string
  containerList?: InquiryContainerVO[]
  [key: string]: unknown
}

/** 整箱报价费用条目（供应商匿名报价） */
export interface QuotationFclChargingItemVO {
  id?: string
  quotationId?: string
  project?: string
  chargeItemId?: string | number
  measureId?: string
  /** 计费单位 code（box=按箱） */
  measure?: string
  quantity?: number
  unitRate?: number | string
  /** 箱型价格 JSON：{ "20GP": 150, "40HQ": 260 } */
  eqPrice?: Record<string, number> | string
  /** 单价-卖价 */
  unitRateSell?: number | string
  /** 箱型卖价价格 JSON：{ "20GP": 150, "40HQ": 260 } */
  eqPriceSell?: Record<string, number> | string
  amount?: number
  currency?: string
  /** 费用主类型：1=港前 2=干线 3=尾程 */
  feeMainType?: number
  /** 费用类型：见文档枚举 */
  feeType?: number
  /** 计费单位类型：1=按箱 2=非按箱 */
  billingUnitType?: number
  /** 支付方式：1=预付 2=到付 */
  paymentType?: number
  /** 潜在费用类型：1=海运附加费 2=起运港费用 3=目的港费用 */
  potentialFeeType?: number
  remarks?: string
  [key: string]: unknown
}

/** 整箱报价方案（供应商匿名报价） */
export interface QuotationFclListVO {
  id?: string
  quotationNo?: string
  status?: number
  inquiryId?: string
  inquiryNo?: string
  supplierId?: string
  supplierName?: string
  contactId?: string
  contactPhone?: string
  contactEmail?: string
  quoterId?: string
  consultationStatus?: number
  entrustConfirmedQuotation?: boolean
  remarks?: string
  supplierRemarks?: string
  onePrice?: boolean
  onePriceSupported?: boolean
  validFrom?: string
  validTo?: string
  etd?: string
  eta?: string
  vessel?: string
  voyage?: string
  transportationDays?: number
  weekdays?: number[]
  transferPortInfo?: string[]
  carrierId?: string
  loadingPortId?: string
  dischargingPortId?: string
  destinationPortId?: string
  transferPortId1?: string
  transferPortId2?: string
  transferPortId3?: string
  directTransit?: number
  cargoType?: number
  paymentTerm?: string
  loadingCountryId?: string
  loadingCityId?: string
  destinationCountryId?: string
  destinationCityId?: string
  hsCode?: string
  quotationDeadline?: string
  specialRequirements?: string
  serviceType?: string
  containerList?: InquiryContainerVO[]
  chargingItemList?: QuotationFclChargingItemVO[]
  files?: BusinessFileVO[]
  // 港前
  pPickupPortId?: string
  pCarrierId?: string
  pVessel?: string
  pVoyage?: string
  pBargeEtd?: string
  pBargeEta?: string
  pTransportationDays?: number
  pPickupRailwayStationId?: string
  pRailCompanyId?: string
  pRailLineType?: string
  pRailTrainNo?: string
  pRailDepartureTime?: string
  pLoadingPortId?: string
  pStuffingTerminalId?: string
  pLoadingRailwayStationId?: string
  pLoadingAirportId?: string
  pAirportTerminal?: string
  // 尾程
  lDischargingPortId?: string
  lDeliveryTerminalId?: string
  lDischargingStationId?: string
  lDischargingAirportId?: string
  lAirportTerminal?: string
  lDestinationPortId?: string
  lVessel?: string
  lVoyage?: string
  lBargeEtd?: string
  lBargeEta?: string
  lTransportationDays?: number
  lDestinationStationId?: string
  lRailCompanyId?: string
  lRailLineType?: string
  lRailTrainNo?: string
  lRailDepartureTime?: string
  /** 港前备注 */
  preRemarks?: string
  /** 尾程备注 */
  lastRemarks?: string
  [key: string]: unknown
}

/** 整箱匿名报价详情（含询价信息） */
export interface QuotationFclAnonymousDetailVO {
  /** 报价单列表 */
  quotations?: QuotationFclListVO[]
  /** 询价单信息 */
  inquiry?: InquiryFclListVO
  /** 供应商询价信息 */
  supplierInquiryVO?: SupplierInquiryVO
}

export const getAnonymousQuotationFclById = (id: string | number, access: LinkAccess) =>
  request.post<QuotationFclListVO>('/price/quotation/fcl/v2/anonymous/get', { id, token: access.token })

/**
 * 通过 token 查询邮件授权范围内的整箱报价方案列表（含询价信息）
 * 后端：QuotationFclController#queryByInquiryAnonymous，返回 QuotationFclAnonymousDetailVO
 */
export const queryByInquiryFclAnonymous = (access: LinkAccess) =>
  request.post<QuotationFclAnonymousDetailVO>('/price/quotation/fcl/v2/anonymous/queryByInquiry', { token: access.token })

export const createAnonymousQuotationFclScheme = (quotation: Record<string, unknown>, access: LinkAccess) =>
  request.post<number>('/price/quotation/fcl/v2/anonymous/createScheme', { quotation, token: access.token })

export const updateAnonymousQuotationFcl = (quotation: Record<string, unknown>, access: LinkAccess) =>
  request.post<boolean>('/price/quotation/fcl/v2/anonymous/update', { quotation, token: access.token })

export const batchSubmitAnonymousQuotationFcl = (
  quotations: Record<string, unknown>[],
  access: LinkAccess,
  supplierInquiryVO?: SupplierInquiryVO
) =>
  request.post<boolean>('/price/quotation/fcl/v2/anonymous/batchSubmit', { quotations, token: access.token, supplierInquiryVO })

export const batchDeleteAnonymousQuotationFcl = (ids: (string | number)[], access: LinkAccess) =>
  request.post<boolean>('/price/quotation/fcl/v2/anonymous/batchDelete', { ids, token: access.token })

export const confirmConsultationAnonymousQuotationFcl = (
  quotations: Record<string, unknown>[],
  access: LinkAccess,
  supplierInquiryVO?: SupplierInquiryVO
) =>
  request.post<boolean>('/price/quotation/fcl/v2/anonymous/confirmConsultation', { quotations, token: access.token, supplierInquiryVO })

export const initiateConsultationByInquirerFcl = (token: string, quotationId: string | number, remarks?: string) =>
  request.post<boolean>('/price/quotation/fcl/v2/anonymous/initiateConsultationByInquirer', { token, quotationId, remarks })

